import { RefiAnalytics } from '@cof/refi-web-sdk';
import { DebugService } from './shared/services/debug.service.js';
import { SessionService } from './core/services/session/session.service.js';
import { Router } from './router.js';
import {
  REFI_CREDITPULL_CONST,
  REFI_PAGES,
  REFI_STATES,
} from './shared/constants/app.constants.js';
import { environment } from '../environment/environment.js';
import { ExperimentService } from './shared/services/experiment.service.js';
import { HostAppEventsService } from './shared/services/host-app-events.service.js';

class _AppService {
  constructor() {
    this.debug = new DebugService('AppService');
    this.testingParam = new URL(window.location.href)?.searchParams?.get(
      'testing'
    );
    this.refreshSession();
    document.dispatchEvent(
      new CustomEvent('glia-add-custom-attributes', {
        detail: { AppId: this.session.currentApp?.appId || '' },
      })
    );
    this.analytics = new RefiAnalytics('creditpull', [
      'sitecatalyst',
      'potomac',
    ]);
    this._experiments = {};
  }

  refreshSession() {
    this.session = SessionService.session || {};
    if (ExperimentService.isDelayedHardpullToggledOn) {
      this._currentState = REFI_STATES.DELAYED_HARDPULL_PRIMER;
    } else if (ExperimentService.isIconicHardpullAndToggledOn) {
      this._currentState = 'iconic-credit-consent';
    } else if (ExperimentService.isCombinedHardpullContractToggledOn) {
      this._currentState = REFI_STATES.COMBINED_HP_CONTRACT;
    } else {
      this._currentState = REFI_STATES.CREDIT_CONSENT;
    }

    if (this.testingParam === 'lighthouse-consent' && !environment.isProdEnv) {
      this.session = SessionService.mockConsentSession;
      this._currentState = REFI_STATES.CREDIT_CONSENT;
    }
    if (
      this.testingParam === 'lighthouse-combined-hp-cont' &&
      !environment.isProdEnv
    ) {
      this.session = SessionService.mockHardpullCombinedContractSession;
      this._currentState = REFI_STATES.COMBINED_HP_CONTRACT;
    }
    if (this.testingParam === 'lighthouse-review' && !environment.isProdEnv) {
      this.session = SessionService.mockReviewingSession;
      this._currentState = REFI_STATES.HARDPULL_REVIEWING;
    }
    if (this.testingParam === 'softpull' && !environment.isProdEnv) {
      this._currentState = 'prequalify-review';
      this._creditpullType = REFI_CREDITPULL_CONST.CREDITPULL_TYPE.SOFTPULL;
    }
    if (
      this.testingParam === 'lighthouse-delayed-hardpull-primer' &&
      !environment.isProdEnv
    ) {
      this.session = SessionService.mockIconicHardpullSession;
      this._currentState = REFI_STATES.DELAYED_HARDPULL_PRIMER;
    }
    if (
      this.testingParam === 'lighthouse-delayed-hardpull-consent' &&
      !environment.isProdEnv
    ) {
      this.session = SessionService.mockIconicHardpullSession;
      this._currentState = 'delayed-hardpull-consent';
    }
    this._state = {
      appStatusId: this.session.currentApp?.appStatusId,
      statFlag: this.session.currentApp?.statFlag,
    };
    this._appExpDate = new Date(this.session.currentApp?.validityEndDate);
    this._status = '';
    this._hasStips = false;
    this._otpPhoneNumber = null;
    this._otpTrackingNumber = null;
  }

  get appExpDate() {
    this.debug.log('getting appExpDate', this._appExpDate);
    return this._appExpDate.toLocaleDateString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  get creditpullType() {
    return this._creditpullType;
  }

  set creditpullType(creditpullType) {
    this._creditpullType = creditpullType;
  }

  get status() {
    this.debug.log('getting status', this._status);
    return this._status;
  }

  set status(status) {
    this._status = status;
  }

  get state() {
    return this._state;
  }

  set state(state) {
    this._state = state;
  }

  get currentState() {
    return this._currentState;
  }

  set currentState(currentState) {
    this._currentState = currentState;
  }

  get hasCoBorrower() {
    return !!this.session?.hasCoBorrower;
  }

  get hasStips() {
    return this._hasStips;
  }

  set hasStips(hasStips) {
    this._hasStips = hasStips;
    document.dispatchEvent(
      new CustomEvent('refi-stips-update', {
        detail: this._hasStips,
      })
    );
  }

  get isV2Routing() {
    return this._isV2Routing;
  }

  set isV2Routing(isV2Routing) {
    this._isV2Routing = isV2Routing;
  }

  get otpPhoneNumber() {
    return this._otpPhoneNumber;
  }

  set otpPhoneNumber(otpPhoneNumber) {
    this._otpPhoneNumber = otpPhoneNumber;
  }

  get otpTrackingNumber() {
    return this._otpTrackingNumber;
  }

  set otpTrackingNumber(otpTrackingNumber) {
    this._otpTrackingNumber = otpTrackingNumber;
  }

  updateSoftpullState(status, state) {
    this._status = status;
    this._currentState = state;
    if (this.isV2Routing) {
      // V2 Host app doesn't need the 'softpull/' at the start of the status
      const v2Status = this._status.replace('softpull/', '');
      HostAppEventsService.sendNextStepEvent(v2Status);
      this.debug.log('V2 routing, sending next step event', this._status);
    } else {
      Router.goTo(`${this._status}`);
    }
  }

  updateState({
    appStatusId = this.state.appStatusId,
    statFlag = this.state.statFlag,
    updateRoute = true,
  }) {
    this.debug.log('updateState called with', {
      appStatusId,
      statFlag,
    });
    this.state = { appStatusId, statFlag };
    SessionService.session = { appStatusId, statFlag };

    if (updateRoute) {
      this.updateRoute(this.state);
    }
  }

  updateRoute(state) {
    this.debug.log('routing with state', state);
    switch (Number(state.appStatusId)) {
      case 400:
        this._status = REFI_PAGES.HARDPULL_PROCESSING;
        this._currentState = REFI_STATES.HARDPULL_PROCESSING;
        break;
      case 402:
        this._status = REFI_PAGES.HARDPULL_DECLINED;
        this._currentState = REFI_STATES.HARDPULL_DECLINED;
        break;
      case 405:
      case 406:
      case 407:
      case 408:
        this._status = this.setStateFromStatFlag(state);
        break;
      default:
        this._currentState = REFI_STATES.HARDPULL_REVIEWING;
        this._status = REFI_PAGES.HARDPULL_REVIEWING;
    }

    if (this.isV2Routing) {
      HostAppEventsService.sendNextStepEvent(this._status);
    } else {
      Router.goTo(`${this._status}`);
      Router.sendStateChangedEvent(`${this._status}`);
    }
  }

  setStateFromStatFlag(state) {
    this.debug.log(`setStateFromStatFlag: ${JSON.stringify(state)}`);
    let page = REFI_PAGES.CREDIT_CONSENT;
    switch (Number(state.statFlag)) {
      case 11:
        if (
          window.location.href.includes('e-sign') &&
          ExperimentService.isCombinedHardpullContractToggledOn
        ) {
          this._currentState = REFI_STATES.CXT_COMBINED_HARDPULL;
          return REFI_PAGES.CXT_COMBINED_HARDPULL;
        }
        this._currentState = REFI_STATES.CREDIT_CONSENT;
        if (ExperimentService.isCombinedHardpullContractToggledOn) {
          this._currentState = REFI_STATES.COMBINED_HP_CONTRACT;
          page = REFI_PAGES.COMBINED_HP_CONTRACT;
        }
        if (ExperimentService.isDelayedHardpullToggledOn) {
          this._currentState = REFI_STATES.DELAYED_HARDPULL_PRIMER;
          page = REFI_PAGES.DELAYED_HARDPULL_PRIMER;
        }

        return page;
      default:
        // TODO: What should we do here?
        return '';
    }
  }

  // Translate Loan Status String to number
  appStatusIdNumber(appStatusId) {
    let returnValue;
    if (Number.isNaN(Number(appStatusId))) {
      switch (appStatusId.toUpperCase()) {
        case 'STUCK':
          returnValue = 400;
          break;
        case 'CANCELED':
          returnValue = 401;
          break;
        case 'DECLINED':
          returnValue = 402;
          break;
        case 'REVIEWING':
          returnValue = 403;
          break;
        case 'VERIFYING':
          returnValue = 404;
          break;
        case 'APPROVED':
          returnValue = 405;
          break;
        case 'FUNDING':
          returnValue = 406;
          break;
        case 'FUNDED':
          returnValue = 407;
          break;
        case 'PENDING':
          returnValue = 408;
          break;
        case 'RETURNED':
          returnValue = 409;
          break;
        case 'EXPIRED':
          returnValue = 410;
          break;
        case 'PAIDPREFUND':
          returnValue = 411;
          break;
        case 'PREFUNDING':
          returnValue = 421;
          break;
        case 'POSTFUNDING':
          returnValue = 422;
          break;
        case 'AUDIT_REVIEW':
          returnValue = 500;
          break;
        default:
          returnValue = -1;
          break;
      }
    } else {
      returnValue = appStatusId;
    }
    return returnValue;
  }

  setAnalyticsApp(appId) {
    this.analytics.appId = appId;
  }

  removeVisibilityTracker() {
    this.analytics.removeVisibilityTracker();
  }
}

export const AppService = new _AppService();
